import Checklist, { SpousalInfoSupportingItemInput } from 'jobs/Checklist'
import InfoGatheringStore from 'jobs/InfoGatheringStore'
import Job from 'jobs/Job'
import { computed, makeObservable } from 'mobx'
import { toStream } from 'utils/mobx-util'
import { switchMap } from 'rxjs/operators'
import { whileActive, withCancellation } from 'utils/rx-util'
import { CancellationToken } from 'infra/cancellation'
import { task } from 'mobx-task'
import IndividualProfileStore from '../IndividualProfileStore'
import { BehaviorSubject } from 'rxjs'

export default class SpousalProfileViewModel {
  // Tracks whether the view model is active.
  private readonly _active = new BehaviorSubject<boolean>(false)

  constructor(
    public job: Job,
    public infoGatheringStore: InfoGatheringStore,
    public individualProfileStore: IndividualProfileStore,
    private getChecklist: () => Checklist | undefined,
  ) {
    makeObservable(this)

    // reacts to changes in the supporting item to re-fetch the profile.
    toStream(() => {
      return {
        profileVersion: this.spousalInfoSupportingItemInput?.profileVersion,
        profileId: this.spousalInfoSupportingItemInput?.profileId,
      }
    }, true)
      .pipe(
        whileActive(this._active),
        switchMap(() => withCancellation(this._onRemoteItemUpdated)),
      )
      .subscribe()
  }

  /**
   * The spousal profile version.
   */
  @computed
  get profile() {
    if (!this.spousalInfoSupportingItemInput?.profileVersion) {
      return null
    }

    return this.individualProfileStore.getSpousalProfile(
      this.spousalInfoSupportingItemInput?.profileId,
      this.spousalInfoSupportingItemInput?.profileVersion,
    )
  }

  /**
   * The checklist.
   */
  @computed
  get checklist() {
    return this.getChecklist()
  }

  /**
   * The spousal info section.
   */
  @computed
  get personalInfoSection() {
    return this.checklist?.personalInfoSection
  }

  /**
   * The supporting item.
   */
  @computed
  get spousalInfoSupportingItem() {
    return this.personalInfoSection?.spousalInfoSupportingItem
  }

  /**
   * The supporting item input.
   */
  @computed
  get spousalInfoSupportingItemInput() {
    return this.spousalInfoSupportingItem
      ?.input as SpousalInfoSupportingItemInput
  }

  /**
   * Activates the vm.
   */
  activate = task.resolved(async () => {
    this._active.next(true)

    if (this.spousalInfoSupportingItem?.completed) {
      await this.individualProfileStore.fetchSpousalProfileForJob(this.job.id)
    }
  })

  /**
   * Deactivates the vm.
   */
  deactivate() {
    this._active.next(false)
  }

  /**
   * Updates the spousal profile when remotes update happens.
   */
  private _onRemoteItemUpdated = task(async (ct: CancellationToken) => {
    if (this.spousalInfoSupportingItem?.completed) {
      await ct.race(
        this.individualProfileStore.fetchSpousalProfileForJob(this.job.id),
      )
    }
  })
}
